import * as React from 'react';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { getLocationAddressFields } from '@restworld/utils-common';
import { Variable } from '@restworld/utility-types';

import { WizardStepProps } from '../../worker-application-wizard.types';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';
import { DatePicker, DatePickerValue, FormLabelWrapper, Heading, Button, TextArea } from '@restworld/ui-ds-base';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useExperienceFieldOptions, useSkippedStep } from '../form-inputs.hooks';
import { HelperTextRElement } from '../form-inputs';
import { WizardSelectField, WizardMultiSelect, FormLabelLabel } from '../';
import { useErrorMessage } from '../../wizard-application-wizard.utils';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';

const rootKey = 'past_experiences';
const defaultValue = {};
export const WizardSixthStep: React.FunctionComponent<WizardStepProps> = (props) => {
  const { t } = useTranslation();
  const { getValues, trigger, control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: rootKey });
  const experienceFields = useMemo(
    () =>
      fields.map((field, fieldIndex) => {
        const domainInterestId: string | undefined = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
        return (
          <div key={field.id} className="flex flex-col gap-4">
            <div className="flex justify-start gap-4">
              <Button
                disabled={fields.length === 1}
                onClick={() => remove(fieldIndex)}
                variant="secondary-danger"
                StartIcon={TrashIcon}
              />
              <Heading>{`${t('common:experience')} ${fieldIndex + 1}`}</Heading>
            </div>
            <WizardExperienceContext.Provider
              value={{
                fieldIndex,
                domainInterestId,
                fieldLength: fields.length,
              }}
            >
              <Fields />
              {fields.length - 1 === fieldIndex && (
                <div className="self-center">
                  <Button
                    onClick={async (e) => {
                      e?.stopPropagation();
                      await trigger(`${rootKey}.${fieldIndex}`)
                        .then((r) => {
                          if (r) append(defaultValue, { focusIndex: fieldIndex + 1 });
                        })
                        .catch((e) => console.error(e, 'error on form validation'));
                    }}
                    variant="secondary-restworld"
                  >
                    + {t('onboarding-page:add_experience')}
                  </Button>
                </div>
              )}
            </WizardExperienceContext.Provider>
          </div>
        );
      }),
    [t, append, fields, getValues, remove, trigger]
  );
  useEffect(() => {
    if (!fields.length) append(defaultValue);
  }, [append, fields.length]);
  return <div className="flex flex-col gap-4 md:h-[26rem] md:overflow-y-auto md:px-1">{experienceFields}</div>;
};
export default WizardSixthStep;
const Fields = () => {
  const { fieldIndex, fieldLength } = useWizardExperienceContextCxt();
  const { getValues, setValue } = useFormContext();
  const {
    fieldOptions: { domainInterest, restaurantType },
  } = useWorkerApplicationCxt();
  const { occupationOptions, occupationSkillOptions } = useExperienceFieldOptions({
    occupation: getValues(`${rootKey}.${fieldIndex}.occupation_id`),
    domainInterestId: getValues(`${rootKey}.${fieldIndex}.domain_interest`),
    skillId: `${rootKey}.${fieldIndex}.skills`,
    setValue,
    domainInterest,
  });
  return (
    <>
      <DomainInterestField options={domainInterest} />
      {occupationOptions?.length ? <OccupationComponent options={occupationOptions} /> : null}
      <RestaurantType options={restaurantType} />
      <AutoCompleteFields />
      <JobDescription />
      <JobInterval />
      <Skills options={occupationSkillOptions} />
      {fieldLength > 1 && <hr />}
    </>
  );
};
const AutoCompleteFields = () => {
  const { getValues, setValue, watch, control } = useFormContext();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const isSkipped = useSkippedStep();
  const { t } = useTranslation();
  // const id = `${rootKey}.${fieldIndex}.restaurant_name` as const;
  // const errorMessage = useErrorMessage({ id: 'restaurant_name', fieldIndex, rootKey });
  const domainInterestId = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
  const registerAddressDataToForm = React.useCallback(
    // placeId geocodes the exact selected place: same billing as free-text, but no
    // ambiguity for similarly-named venues (chains).
    async ({ description, placeId }: { description: string; placeId?: string }) => {
      const addressData = await getLocationAddressFields(description, placeId, 'worker_signup_experience_address');
      if (addressData) {
        setValue(`${rootKey}.${fieldIndex}.address_lat`, addressData.address_lat);
        setValue(`${rootKey}.${fieldIndex}.address_lon`, addressData.address_lon);
        setValue(`${rootKey}.${fieldIndex}.country`, addressData.country);
        setValue(`${rootKey}.${fieldIndex}.administrative_area_level_1`, addressData.administrative_area_level_1);
        setValue(`${rootKey}.${fieldIndex}.administrative_area_level_2`, addressData.administrative_area_level_2);
        setValue(`${rootKey}.${fieldIndex}.postal_code`, addressData.postal_code);
        setValue(`${rootKey}.${fieldIndex}.city`, addressData.locality);
      }
    },
    [fieldIndex, setValue]
  );

  return (
    <>
      <GoogleAutoCompleteInput
        mainLabel={`${t('onboarding-page:name_of_the_venue')}*`}
        formId={`${rootKey}.${fieldIndex}.restaurant_name` as const}
        key={`${rootKey}.${fieldIndex}.restaurant_name` as const}
        placeholder={t('onboarding-page:enter_the_name_of_the_venue')}
        disabled={!domainInterestId}
        placesFilterTypes={['establishment']}
        trackingSurface="worker_signup_experience_venue"
        onSuggestionSelect={async (placesData) => {
          const { description, structured_formatting: descriptionObject } = placesData;
          setValue(`${rootKey}.${fieldIndex}.restaurant_name`, descriptionObject?.main_text);
          setValue(`${rootKey}.${fieldIndex}.gmaps_place_id`, placesData.place_id);
          setValue(`${rootKey}.${fieldIndex}.gmaps_last_updated_at`, new Date().toISOString());

          setValue(`${rootKey}.${fieldIndex}.address`, descriptionObject.secondary_text);
          registerAddressDataToForm({ description, placeId: placesData.place_id });
        }}
        required={!isSkipped}
        title={t('onboarding-page:dont_see_the_place_among_the_options')}
        subTitle={t('onboarding-page:fill_in_the_name_and_then_add_the_address')}
        testId="venue-name-input"
        control={control}
      />
      <Controller
        name={`${rootKey}.${fieldIndex}.address` as const}
        control={control}
        rules={{ required: true }}
        render={({ field }) => {
          return (
            <GoogleAutoCompleteInput
              mainLabel={t('common:address')}
              formId={`${rootKey}.${fieldIndex}.address` as const}
              key={`${rootKey}.${fieldIndex}.address` as const}
              placeholder="Via/Piazza"
              disabled={Boolean(!domainInterestId || !watch(`${rootKey}.${fieldIndex}.restaurant_name`))}
              placesFilterTypes={['address']}
              trackingSurface="worker_signup_experience_address"
              onSuggestionSelect={async (placesData) => {
                const { description, structured_formatting: descriptionObject } = placesData;
                setValue(`${rootKey}.${fieldIndex}.address`, descriptionObject?.main_text);
                registerAddressDataToForm({ description, placeId: placesData.place_id });
              }}
              title={t('onboarding-page:cant_find_the_correct_address')}
              subTitle={t('onboarding-page:fill_it_in_manually_or_choose_the_closest_one')}
              testId="venue-address-input"
              formValue={field.value || ''}
              // control={control}
            />
          );
        }}
      />
    </>
  );
};

const DomainInterestField = ({ options }: { options: Variable[] }) => {
  const { t } = useTranslation();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({ id: 'domain_interest', fieldIndex, rootKey });
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:choose_sector')}
      id={`${rootKey}.${fieldIndex}.domain_interest` as const}
      formLabel={t('common:industry_type')}
      rules={{ required: !isSkipped }}
      errorMessage={errorMessage}
      mainTitle={t('common:industry_type')}
      testId="worker-sector-options"
      inputTestId="worker-sector-input"
    />
  );
};
const OccupationComponent = ({ options }: { options: Variable[] }) => {
  const { t } = useTranslation();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({ id: 'occupation_id', fieldIndex, rootKey });
  const { getValues } = useFormContext();
  const domainInterestId = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:choose_role')}
      id={`${rootKey}.${fieldIndex}.occupation_id` as const}
      formLabel={t('common:role')}
      disabled={!domainInterestId}
      rules={{ required: !isSkipped }}
      errorMessage={errorMessage}
      testId="worker-role-options"
      inputTestId="worker-role-input"
    />
  );
};
const RestaurantType = ({ options }: { options: Variable[] }) => {
  const { t } = useTranslation();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const errorMessage = useErrorMessage({ id: 'restaurant_type', fieldIndex, rootKey });
  const { getValues } = useFormContext();
  const domainInterestId = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:choose_type')}
      id={`${rootKey}.${fieldIndex}.restaurant_type` as const}
      formLabel={<FormLabelLabel label={t('onboarding-page:type_of_venue')} isOptional />}
      mainTitle={t('onboarding-page:type_of_venue')}
      disabled={!domainInterestId}
      rules={{ required: false }}
      errorMessage={errorMessage}
      testId="industry-type-options"
      inputTestId="industry-type-input"
    />
  );
};
const JobDescription = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const id = `${rootKey}.${fieldIndex}.description` as const;
  const errorMessage = useErrorMessage({ id: 'description', fieldIndex, rootKey });
  const { getValues } = useFormContext();
  const domainInterestId = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
  return (
    <FormLabelWrapper
      disabled={!domainInterestId}
      id={id}
      label={<FormLabelLabel label={t('onboarding-page:brief_job_description')} isOptional />}
    >
      <Controller
        name={id}
        control={control}
        rules={{ required: false }}
        render={({ field }) => (
          <TextArea
            {...field}
            disabled={!domainInterestId}
            id={id}
            placeholder={t('onboarding-page:free_text')}
            supportText={errorMessage as string}
            isError={!!errorMessage}
          />
        )}
      />
    </FormLabelWrapper>
  );
};
const JobInterval = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [, setStartDate] = useState<DatePickerValue>();
  const [, setEndDate] = useState<DatePickerValue>();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const isSkipped = useSkippedStep();
  const toDateId = `${rootKey}.${fieldIndex}.to_date` as const;
  const fromDateId = `${rootKey}.${fieldIndex}.from_date` as const;
  const errorMessageTo = useErrorMessage({ id: 'to_date', fieldIndex, rootKey });
  const errorMessageFrom = useErrorMessage({ id: 'from_date', fieldIndex, rootKey });
  const { getValues } = useFormContext();
  const domainInterestId = getValues(`${rootKey}.${fieldIndex}.domain_interest`);
  return (
    <FormLabelWrapper
      disabled={!domainInterestId}
      id={'job_interval'}
      label={
        <FormLabelLabel
          label={t('onboarding-page:in_what_period_did_you_work')}
          optionalText={t('onboarding-page:dates_may_be_indicative')}
        />
      }
    >
      <div className="flex flex-col gap-2 lg:flex-row">
        <Controller
          name={fromDateId}
          control={control}
          rules={{ required: !isSkipped }}
          render={({ field }) => {
            return (
              <DatePicker
                helperText={errorMessageFrom as string}
                fieldState={errorMessageFrom ? 'error' : 'normal'}
                id={fromDateId}
                disabled={!domainInterestId}
                selectRange={false}
                {...{
                  ...field,
                  onChange(date) {
                    setStartDate(date);
                    field.onChange(date);
                  },
                }}
                testId="job-start-date"
              />
            );
          }}
        />
        <Controller
          name={toDateId}
          control={control}
          rules={{ required: !isSkipped }}
          render={({ field }) => {
            return (
              <DatePicker
                helperText={errorMessageTo}
                fieldState={errorMessageFrom ? 'error' : 'normal'}
                id={toDateId}
                disabled={!domainInterestId}
                selectRange={false}
                {...{
                  ...field,
                  onChange(date) {
                    setEndDate(date);
                    field.onChange(date);
                  },
                }}
                testId="job-end-date"
              />
            );
          }}
        />
      </div>
    </FormLabelWrapper>
  );
};
const Skills = ({ options }: { options: Variable[] }) => {
  const { t } = useTranslation();
  const { fieldIndex } = useWizardExperienceContextCxt();
  const id = `${rootKey}.${fieldIndex}.skills` as const;
  const isSkipped = useSkippedStep();
  const errorMessage = useErrorMessage({ id: 'skills', fieldIndex, rootKey });
  return options?.length ? (
    <WizardMultiSelect
      options={options}
      id={id}
      formLabel={t('onboarding-page:skills_acquired')}
      rules={{ required: !isSkipped }}
      errorMessage={errorMessage}
      isModal={false}
    />
  ) : null;
};
const WizardExperienceContext = React.createContext<IWizardExperienceContextType | null>(null);
const useWizardExperienceContextCxt: () => IWizardExperienceContextType = () => {
  const currentCxtValues = useContext(WizardExperienceContext);
  if (!currentCxtValues)
    throw new Error('useWizardExperienceContextCxt has to be used within <WizardExperienceContext.Provider>');
  return currentCxtValues;
};
interface IWizardExperienceContextType {
  fieldIndex: number;
  domainInterestId?: string;
  fieldLength: number;
}
